import React, { useState } from 'react';
import styles from './CustomerSideMenu.module.scss';
import { useTenant } from '../../../tenant/branding/useTenant';
import { useCanSeeMenuItem } from '../../../guards/useCanSeeMenuItem';
import { menuItems } from './CustomerMenu.constants';
import { CustomerSideMenuProps } from './CustomerMenu.types';
import { BellNotificationIcon, HiddenSidebar, LogoutIcon, ViewSidebar } from '../../../assets/customermenu-icons';
import { useNavigate } from 'react-router-dom';
import { ApplicationRoutes } from '../../../routes';
import { useSubMenuVisibility } from './customerMenu.visibility';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../stores/store';
import { notificationApi, useLazyGetUnreadNotificationsCountQuery } from '../../../service/notification-service';
import { setUnreadCount } from '../../../stores/slices/notification-slice';
import { useTheme } from '../../../theme/useTheme';
import LoadedImage from '../../../common/components/loaded-image/LoadedImage';

const SunIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<circle cx="12" cy="12" r="4.2" />
		<path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.55 1.55M17.85 17.85l1.55 1.55M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.55-1.55M17.85 6.15l1.55-1.55" />
	</svg>
);

const MoonIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		<path d="M20.5 14.5a8.5 8.5 0 1 1-11-11 0.6 0.6 0 0 1 0.79 0.79 6.6 6.6 0 0 0 9.42 9.42 0.6 0.6 0 0 1 0.79 0.79z" />
	</svg>
);


const CustomerSideMenu: React.FC<CustomerSideMenuProps> = ({
	activeMenu = "dashboard",
	activeSubMenu,
	onMenuClick,
	onSubMenuClick,
	userName = "Customer",
	onLogOutClick,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { filterVisibleItems } = useSubMenuVisibility();
  const [isExpanded, setIsExpanded] = useState(false);
  const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);
  const [fetchUnreadCount] = useLazyGetUnreadNotificationsCountQuery();
  const { mode, setPreference } = useTheme();
  const { logo, name } = useTenant();
  const canSeeMenuItem = useCanSeeMenuItem();

  const handleThemeToggle = () => {
    setPreference(mode === 'dark' ? 'light' : 'dark');
  };

	const handleMenuClick = (menuId: string, path: string) => {
		if (path) navigate(path);
		onMenuClick?.(menuId);
	};

	const handleSubMenuClick = (subMenuId: string, path: string) => {
		if (path) navigate(path);
		onSubMenuClick?.(subMenuId);
	};

	const handleNotificationsClick = async () => {
		dispatch(notificationApi.util.invalidateTags(['Notifications']));
		const result = await fetchUnreadCount();
		if (result.data !== undefined) dispatch(setUnreadCount(result.data));
		navigate(`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}`);
	};

	// Find the active menu item to get its submenus
	const activeMenuItem = menuItems.find(item => item.id === activeMenu);
	const visibleSubMenuItems = filterVisibleItems(activeMenuItem?.subMenuItems);
	const hasSubMenu = activeMenuItem?.subMenuItems && activeMenuItem.subMenuItems.length > 0;

	return (
		<div className={styles.menuContainer}>
			<aside
				className={`${styles.sidebar} ${hasSubMenu ? styles.activeSubMenu : ""} ${isExpanded ? styles.expanded : ""}`}
			>
				{/* Logo */}
					<div className={styles.logoIcon}>
						<LoadedImage src={logo} alt={name} />
					</div>

        {/* Main Navigation */}
        <nav className={styles.nav}>
          <div className={styles.menuItems}>
            {menuItems.filter(canSeeMenuItem).map((item) => (
              <button
                key={item.id}
                className={`${styles.menuItem} ${
                  activeMenu === item.id ? styles.active : ''
                }`}
                onClick={() => handleMenuClick(item.id, item.path)}
                title={item.label}
              >
                <div className={styles.menuIcon}>{activeMenu === item.id ? item.activeIcon : item.icon}</div>
                <span className={styles.menuLabel}>{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Notification Bell */}
        <div className={styles.notificationSection}>
          <button
            className={styles.notificationBtn}
            onClick={handleNotificationsClick}
            title="Notifications"
          >
            <div className={styles.notificationIconWrapper}>
              <BellNotificationIcon />
              {unreadCount > 0 && (
                <span className={styles.notificationBadge}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <span className={styles.menuLabel}>Notifications</span>
          </button>
        </div>

			{/* Theme toggle */}
			<div className={styles.notificationSection}>
				<button
					className={styles.notificationBtn}
					onClick={handleThemeToggle}
					title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
					aria-label="Toggle theme"
				>
					<div className={styles.menuIcon}>
						{mode === 'dark' ? <SunIcon /> : <MoonIcon />}
					</div>
					<span className={styles.menuLabel}>
						{mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
					</span>
				</button>
			</div>

			{/* Logout */}
			<div className={styles.notificationSection}>
				<button className={styles.notificationBtn} onClick={onLogOutClick} title="Logout">
					<div className={styles.menuIcon}>
						<LogoutIcon/>
					</div>
					<span className={styles.menuLabel}>Logout</span>
				</button>
			</div>


			{/* Expand / Collapse toggle */}
			<div className={styles.notificationSection}>
				<button
					className={styles.sidebarStateBtn}
					onClick={() => setIsExpanded(prev => !prev)}
				>
					<div className={styles.menuIcon}>
						{
							isExpanded ? <HiddenSidebar/> : <ViewSidebar/>
						}
					</div>
				</button>
			</div>

			</aside>

			{/* Submenu Panel */}
			{hasSubMenu && (
				<aside className={styles.submenuPanel}>
					<div className={styles.submenuHeader}>
						<h3 className={styles.submenuTitle}>{activeMenuItem.label}</h3>
					</div>
					<nav className={styles.submenuNav}>
						<div className={styles.submenuItems}>
							{visibleSubMenuItems?.map(subItem => (
								<button
									key={subItem.id}
									className={`${styles.submenuItem} ${activeSubMenu === subItem.id ? styles.active : ""}`}
									onClick={() => handleSubMenuClick(subItem.id, subItem.path)}
								>
									{subItem.label}
								</button>
							))}
						</div>
					</nav>
				</aside>
			)}
		</div>
	);
};

export default React.memo(CustomerSideMenu);
